// Shared by the sales screens: load a quote, keep it fresh after every write, plain helpers.
import restApi from '../../api/index.js'
import { money, fmtDate, errorText } from './pe-util'

export default {
	data() {
		return { quote: null, loadingQuote: false, savedAt: null, busy: false }
	},
	computed: {
		quoteId() { return this.$route.params.id },
		isAdmin() { const u = this.$store.state.authUser; return !!u && u.role === 'Admin' },
		verdict() {
			const q = this.quote
			if (!q) return null
			if (!q.lines.length) return { tone: 'info', title: 'Nothing on the quote yet.', text: 'Pick what we are quoting and answer the questions; the price builds up here.' }
			if (q.category === 'standard') return { tone: 'ok', title: 'Standard price. You can send this.', text: 'Every item is on the current price list' + (q.population_snapshot ? ' for a population of ' + Number(q.population_snapshot).toLocaleString() : '') + '. Fixed for ' + q.term_years + ' years.' }
			if (q.decision_at) return { tone: 'ok', title: 'Finance has approved this. You can send it.', text: q.decision_rationale || '' }
			return { tone: 'warn', title: 'Finance has to look at this first', text: this.plainReason(q.category_reason) + ' Finance usually answers within 2 business days.' }
		},
	},
	methods: {
		money, fmtDate,
		toast(title, icon = 'error') { this.$root.$emit('pe-notice', { tone: icon === 'success' ? 'ok' : (icon === 'info' ? 'info' : 'warn'), text: title }) },
		plainReason(reason) {
			if (!reason) return ''
			return reason.split('; ').map(r => r
				.replace(/^custom line needs a price: (.*)$/, 'One item has no list price: $1.')
				.replace(/^custom line: (.*)$/, 'One item is custom: $1.')
				.replace(/^provisional price, product owner to confirm: (.*)$/, 'The price of $1 is provisional until the product owner confirms it.')
				.replace(/^price request: target (.*)$/, 'You asked for a different price ($1 a year).')
				.replace(/^discount without a published floor and cap: (.*)$/, 'There is a discount on $1 that finance has to approve.')
				.replace(/^discount requested by sales: (.*)$/, 'You gave a discount on $1. Finance approves it before the quote goes out.')
				.replace(/^no population on record$/, 'This town has no population on record, so the prices assume the smallest size. Enter the population on the Build screen, or finance checks the quote.')
				.replace(/^below floor: (.*)$/, 'The price of $1 is below its floor.')
				.replace(/^over the discount cap: (.*)$/, 'The discount on $1 is above the limit.')
				.replace(/^not on a published schedule: (.*)$/, '$1 is not on a published price list.')
				.replace(/^add-on stacking on a bundle.*$/, 'Two or more HeyGov add-ons on a bundle: finance confirms the stacked price.')
				.replace(/^over the approval threshold$/, 'The total is above the amount sales can send alone.')).join(' ')
				.replace(/\b[A-Z]{2,3}-[A-Z0-9-]+\b/g, code => { const l = this.quote && this.quote.lines.find(x => x.sku === code); return l ? l.name.replace(/ \(.*\)$/, '') : code }) // the line's name, not its code
		},
		loadQuote() {
			this.loadingQuote = true
			return restApi.get('/pricing/quotes/' + this.quoteId).then(({ data }) => { this.quote = data; return data }).catch(err => { this.toast(errorText(err, 'Could not load the quote')); throw err }).finally(() => { this.loadingQuote = false })
		},
		apply(promise) {
			// every write returns the recalculated quote
			this.busy = true
			return promise.then(({ data }) => { this.quote = data; this.savedAt = new Date(); return data }).catch(err => { this.toast(errorText(err, 'Save failed')); throw err }).finally(() => { this.busy = false })
		},
		addLine(body) { return this.apply(restApi.post('/pricing/quotes/' + this.quoteId + '/lines', body)) },
		updateLine(lineId, body) { return this.apply(restApi.put('/pricing/quotes/' + this.quoteId + '/lines/' + lineId, body)) },
		removeLine(lineId) { return this.apply(restApi.delete('/pricing/quotes/' + this.quoteId + '/lines/' + lineId)) },
		updateQuote(body) { return this.apply(restApi.put('/pricing/quotes/' + this.quoteId, body)) },
		transition(to, payload) { return this.apply(restApi.post('/pricing/quotes/' + this.quoteId + '/transition', Object.assign({ to }, payload || {}))) },
		lineFor(code) { return (this.quote ? this.quote.lines : []).find(l => l.sku === code) || null },
		linesOfBrand(brand) { return (this.quote ? this.quote.lines : []).filter(l => l.brand === brand) },
		/** Make the quote's lines of one brand equal to target [{sku_code, qty, period}], in as few calls as possible. */
		async syncLines(brand, target, opts = {}) {
			const current = this.linesOfBrand(brand).filter(l => !l.is_custom)
			const want = new Map(target.map(t => [t.sku_code, t]))
			for (const l of current) {
				if (!want.has(l.sku)) await this.removeLine(l.id)
				else if (want.get(l.sku).qty && want.get(l.sku).qty !== l.qty) await this.updateLine(l.id, { qty: want.get(l.sku).qty })
			}
			for (const t of target) {
				if (!current.find(l => l.sku === t.sku_code)) await this.addLine({ sku_code: t.sku_code, qty: t.qty || 1, period: t.period, suggested: !!opts.suggested })
			}
			return this.quote
		},
	},
}
